import { useSuspenseQuery } from '@apollo/client';
import React, { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { Heading, LinkButton, Stack } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin } from '@eluve/session-helpers';
import { TabNav, useTenantName } from '@eluve/smart-blocks';

import { adminTenantFeatureFlagsFragment } from './components/TenantFeatureFlags';
import {
  TenantIsRealToggle,
  tenantIsRealFragment,
} from './components/TenantIsRealToggle';
import {
  llmOutputTypesFragment,
  tenantLlmOutputsFragment,
} from './components/TenantLlmOutputs';

const adminTenantQuery = graphql(
  `
    query getAdminTenantByIdSummary($tenantId: uuid!) {
      tenantsByPk(id: $tenantId) {
        __typename
        id
        name
        appointmentsAggregate {
          aggregate {
            count
          }
        }
        tenantUsersAggregate {
          aggregate {
            count
          }
        }
        ...AdminTenantFeatureFlags
        ...TenantLlmOutputs
      }
      adminTenants(where: { id: { _eq: $tenantId } }, limit: 1) {
        ...TenantIsReal
      }
    }
  `,
  [
    adminTenantFeatureFlagsFragment,
    tenantLlmOutputsFragment,
    tenantIsRealFragment,
  ],
);

const getLlmOutputTypes = graphql(
  `
    query getLlmOutputTypes {
      ...LlmOutputTypes
    }
  `,
  [llmOutputTypesFragment],
);

export const AdminTenant: React.FC = () => {
  const { tenantId } = useParams() as { tenantId: string };

  const {
    data: { tenantsByPk: tenant },
  } = useSuspenseQuery(adminTenantQuery, {
    variables: {
      tenantId: tenantId,
    },
  });

  useSuspenseQuery(getLlmOutputTypes, {
    context: eluveAdminHasuraContext,
  });

  const name = useTenantName(tenantId);
  const isEluveAdmin = useIsEluveAdmin();
  return (
    <div>
      <div className="my-2 w-full py-2">
        <Heading>{name}</Heading>
        <Stack justify="between" wrap gap={2} direction="horizontal">
          <Heading size="2xl" weight="semibold">{`ID: ${tenantId}`}</Heading>
          <Stack inline fullWidth={false} gap={2} direction="horizontal">
            <TenantIsRealToggle tenantId={tenantId} />
            <LinkButton variant="outline" to="../dash">
              View Organization Dash
            </LinkButton>
            <LinkButton variant="outline" to="../users">
              Manage Users
            </LinkButton>
          </Stack>
        </Stack>
      </div>

      <Stack className="mt-2" gap={4}>
        <Stack direction="horizontal" gap={2} align="stretch">
          <div className="w-full overflow-hidden rounded-lg bg-bg-primary px-4 py-5 shadow-md sm:p-6">
            <dt className="truncate text-sm font-medium text-text-tertiary">
              Appointments
            </dt>
            <dd className="mt-1 text-3xl font-semibold tracking-tight text-text-primary">
              {tenant?.appointmentsAggregate?.aggregate?.count ?? 0}
            </dd>
          </div>
          <div className="w-full overflow-hidden rounded-lg bg-bg-primary px-4 py-5 shadow-md sm:p-6">
            <dt className="truncate text-sm font-medium text-text-tertiary">
              Users
            </dt>
            <dd className="mt-1 text-3xl font-semibold tracking-tight text-text-primary">
              {tenant?.tenantUsersAggregate?.aggregate?.count ?? 0}
            </dd>
          </div>
        </Stack>
        <TabNav
          overflowMode="menu"
          ariaLabel="Tenant navigation"
          tabs={[
            {
              name: 'Feature Flags',
              link: 'feature-flags',
            },
            {
              name: 'LLM Outputs',
              link: 'llm-outputs',
            },
            {
              name: 'Prompt Templates',
              link: 'prompt-templates',
            },
            {
              name: 'LLM Output Templates',
              link: 'llm-output-templates',
            },
            {
              name: 'Imported LLM Output Templates',
              link: 'imported-llm-output-templates',
            },
            {
              name: 'Default User Settings',
              link: 'default-user-settings',
            },
            {
              name: 'Code Types',
              link: 'code-types',
            },
            {
              name: 'Reviewers',
              link: 'reviewers',
            },
            ...(isEluveAdmin
              ? [
                  {
                    name: 'ML Engineers',
                    link: 'ml-engineers',
                  },
                ]
              : []),
            {
              name: 'Prompt Defaults',
              link: 'prompt-defaults',
            },
            {
              name: 'Template Bundles',
              link: 'template-bundles',
            },
            {
              name: 'Conversation Starters',
              link: 'conversation-starters',
            },
          ]}
        />
        <div className="mt-2 w-full">
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </div>
      </Stack>
    </div>
  );
};
